import EzController from 'controllers/ez-base-controller';
import Cookies from 'js-cookie';
import events from 'lib/events';

import SnowplowAnalyticsController from '../snowplow/snowplow-analytics-controller';

const gaEventsListOnce = [
  events.affiliate.login_click,
  events.affiliate.join_now_click,
  events.affiliate.become_affiliate_click,
  events.download_btn.initial_click,
  events.download_btn.match_eezycore_free_dupe,
  events.errors.error404_page,
  events.locale_prompt.locale_prompt_accepted,
  events.locale_prompt.locale_prompt_connects,
  events.locale_prompt.locale_prompt_dismissed,
  events.locale_prompt.switch_language,
  events.search.search_type,
  events.search.without_results,
  events.search.trending_search_click,
  events.sessions.new_account_created,
  events.sessions.user_signup_survey,
  events.sessions.social.login_with_google,
  events.sessions.social.login_with_facebook,
  events.sessions.social.create_account_with_google,
  events.sessions.social.create_account_with_facebook,
  events.show_page.attribution_join_pro_click,
  events.show_page.contributor_click,
  events.show_page.download_ad_modal_connects,
  events.show_page.download_limit_modal_connects,
  events.show_page.favorite,
  events.show_page.editor_button_click,
  events.show_page.file_delivered,
  events.show_page.license_selector_free_modal_closes,
  events.show_page.license_selector_free_modal_connects,
  events.show_page.license_selector_free_modal_free_button,
  events.show_page.license_selector_free_modal_learn_more,
  events.show_page.license_selector_free_modal_pro_button,
  events.show_page.more_info,
  events.show_page.share,
  events.show_page.share_facebook,
  events.show_page.share_html,
  events.show_page.share_pinterest,
  events.show_page.share_twitter,
  events.site_header.explore_brusheezy,
  events.site_header.explore_nav,
  events.site_header.explore_newest,
  events.site_header.explore_cats,
  events.site_header.explore_videezy,
  events.site_header.header_join_pro,
  events.site_header.header_login,
  events.site_header.header_signup,
  events.site_header.header_credits_buy_more,
  events.sponsored_search.position,
  events.sponsored_search.without_results,
  events.checkout.error,
  events.checkout.purchase,
  events.partner.pagination_popup,
  events.partner.search_popup,
  events.collections.login_popup,
  events.collections.account_create,
  events.photos_banner.photos_banner_dismissed, // feature: photos_banner
  events.photos_banner.photos_banner_learn_more, // feature: photos_banner
  events.editor.join_pro,
];

const gaEventsListAlways = [
  events.checkout.inputs.focus,
  events.checkout.inputs.nonHostedFocus,
  events.checkout.inputs.clickSubmitDisabled,
  events.checkout.inputs.clickSubmitEnabled,
  events.checkout.inputs.error,
  events.collections.resource_added,
  events.collections.resource_removed,
  events.collections.add_clicked,
  events.collections.created,
  events.home.featured_click,
  events.partner.results_click,
  events.partner.coupon_click,
  events.partner.infinite_scroll_click,
  events.resources.grid.contributor_info_profile_click,
  events.search.filter,
  events.search.header_search_filter,
  events.search.hide_show_search_filters,
  events.show_page.attribution_link_copied,
  events.show_page.zoom_in,
  events.show_page.related_resource_click,
  events.site_header.related_tag_click,
  events.social.pinterest_share,
  events.ten_download_limit.join_pro,
  events.ten_download_limit.open,
  events.three_download_limit.control_facebook_join,
  events.three_download_limit.control_google_join,
  events.three_download_limit.control_step_one_click,
  events.three_download_limit.control_step_two_click,
  events.three_download_limit.control_pop,
  events.three_download_limit.facebook_join,
  events.three_download_limit.google_join,
  events.three_download_limit.login_click,
  events.three_download_limit.open,
  events.three_download_limit.step_one_click,
  events.three_download_limit.step_two_click,
  events.three_download_limit.unconfirmed_open,
  events.three_download_limit.unconfirmed_send,
  events.vertical_grid.show_more_click,
  events.vertical_grid.end_of_results,
  events.contributor.following,
];

const gaPageViewEventsListAlways = [
  events.vertical_grid.more_results_loaded,
];

export default class GoogleAnalyticsController extends EzController {
  connect() {
    this.snowplow = new SnowplowAnalyticsController();
    this.snowplow.connect(this.snowplowCollector, this.snowplowAppId, this.trackResourceImpressions);
    this.sentPageView = false;

    this.loadGA(window, document, 'script', this.scriptUrl, 'ga');
    this.ga('create', this.trackingId, this.site);

    this.ga((tracker) => {
      Cookies.set('ga-client-id', tracker.get('clientId'));
    });

    if (document.body.getAttribute('data-env') === 'test') {
      this.ga('set', 'checkStorageTask', null); // Don't abort tracking if cookies disabled -- for testing
    }

    this.ga('require', 'displayfeatures');
    this.ga('set', 'anonymizeIp', true);
    this.ga('set', 'dimension4', this.userDimension);

    if (this.userDimension5) {
      this.ga('set', 'dimension5', this.userDimension5);
    }

    this.ga('set', 'dimension6', 'apollo');

    if (this.userDimension7) {
      this.ga('set', 'dimension7', this.userDimension7);
    }

    this.ga('set', 'dimension8', this.userDimension8);
    this.ga('set', 'dimension9', this.userDimension9);

    this.ga('set', 'transport', 'beacon');

    // Set Google Optimize experiment ID
    if (this.optimizeExperimentID && this.optimizeVariantID) {
      this.ga('require', this.optimizeContainerID);
      this.ga('set', 'exp', `${this.optimizeExperimentID}.${this.optimizeVariantID}`);
    }

    this.sendPageView();
    this.checkPostLoginProviderCookie();

    window.addEventListener(events.analytics.fire_page_view, this.sendPageView);

    this.removeListeners(gaEventsListOnce, { once: true });
    this.removeListeners(gaEventsListAlways, { once: false });
    this.removePageViewListeners(gaPageViewEventsListAlways, { once: false });

    this.registerListeners(gaEventsListOnce, { once: true });
    this.registerListeners(gaEventsListAlways, { once: false });
    this.registerPageViewListeners(gaPageViewEventsListAlways, { once: false });

    this.flagLoaded();
  }

  disconnect() {
    this.snowplow.disconnect();
    window.removeEventListener(events.analytics.fire_page_view, this.sendPageView);

    this.removeListeners(gaEventsListOnce, { once: true });
    this.removeListeners(gaEventsListAlways, { once: false });
    this.removePageViewListeners(gaPageViewEventsListAlways, { once: false });
  }

  sendPageView = () => {
    if (!this.sentPageView && !this.isTurbolinksPreview) {
      this.sentPageView = true;
      this.trackPageView(window.location.href);
    }
  }

  // createPageView will send a page view event manually,
  // regardless if a page view has already been sent.
  createPageView = (event) => {
    this.trackPageView(event.detail.url);
  }

  // Checks for a post login cookie for login analytics, cookie created on backend
  checkPostLoginProviderCookie = () => {
    const postLoginProvider = Cookies.get('post_login_provider_event');

    if (postLoginProvider) {
      this.trackEvent({
        type: 'post_login_provider_event',
        detail: {
          gaEventCategory: 'login',
          gaEventAction: 'logged_in',
          gaEventLabel: postLoginProvider,
        },
      });
    }

    // Remove the cookie now that we're done.
    Cookies.remove('post_login_provider_event');
  }

  registerListeners(eventsList, options) {
    eventsList.forEach((eventName) => {
      window.addEventListener(eventName, this.transformEvent, options);
    });
  }

  removeListeners(eventsList, options) {
    eventsList.forEach((eventName) => {
      window.removeEventListener(eventName, this.transformEvent, options);
    });
  }

  registerPageViewListeners(eventsList, options) {
    eventsList.forEach((eventName) => {
      window.addEventListener(eventName, this.createPageView, options);
    });
  }

  removePageViewListeners(eventsList, options) {
    eventsList.forEach((eventName) => {
      window.removeEventListener(eventName, this.createPageView, options);
    });
  }

  transformEvent = (event) => {
    // Clone the event so we don't modify the original and possibly affect other components listening for the event
    const transformedEvent = { ...event };
    transformedEvent.type = event.type; // Carry forward the event string for use in GTM
    transformedEvent.detail = { ...event.detail };

    switch (event.type) {
      case events.editor.join_pro:
        transformedEvent.detail.gaEventCategory = 'editor';
        transformedEvent.detail.gaEventAction = 'join_pro_to_download';
        transformedEvent.detail.gaEventLabel = window.location.href;
        break;

      case events.download_btn.initial_click:
        transformedEvent.detail.gaEventCategory = 'download';
        transformedEvent.detail.gaEventAction = this.transformAnalyticsProToPremium(event.detail.resourceType);
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        transformedEvent.detail.gaNonInteraction = false;
        break;

      case events.download_btn.match_eezycore_free_dupe:
        transformedEvent.detail.gaEventCategory = 'download';
        transformedEvent.detail.gaEventAction = 'free';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        transformedEvent.detail.gaNonInteraction = false;
        break;

      case events.errors.error404_page:
        transformedEvent.detail.gaEventCategory = '404_page';
        transformedEvent.detail.gaEventAction = window.location.pathname;
        break;

      case events.home.featured_click:
        transformedEvent.detail.gaEventCategory = 'homepage';
        transformedEvent.detail.gaEventAction = `curation_box_${event.detail.gridNumber}`;
        transformedEvent.detail.gaEventLabel = event.detail.itemNumber;
        break;

      case events.locale_prompt.locale_prompt_accepted:
        transformedEvent.detail.gaEventCategory = 'locale_prompt';
        transformedEvent.detail.gaEventAction = `${event.detail.currentLocale}_visit_recommend_${event.detail.recommendedLocale}`;
        transformedEvent.detail.gaEventLabel = `visit_in_${event.detail.recommendedLocale}`;
        break;

      case events.locale_prompt.locale_prompt_connects:
        transformedEvent.detail.gaEventCategory = 'locale_prompt';
        transformedEvent.detail.gaEventAction = `${event.detail.currentLocale}_visit_recommend_${event.detail.recommendedLocale}`;
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.locale_prompt.locale_prompt_dismissed:
        transformedEvent.detail.gaEventCategory = 'locale_prompt';
        transformedEvent.detail.gaEventAction = `${event.detail.currentLocale}_visit_recommend_${event.detail.recommendedLocale}`;
        transformedEvent.detail.gaEventLabel = 'no_thanks_stay_here';
        break;

      case events.locale_prompt.switch_language:
        transformedEvent.detail.gaEventCategory = 'locale_prompt';
        transformedEvent.detail.gaEventAction = `${event.detail.currentLocale}_visit_recommend_${event.detail.recommendedLocale}`;
        transformedEvent.detail.gaEventLabel = `view_diff_lang_${event.detail.toLang}`;
        break;

      case events.resources.grid.contributor_info_profile_click:
        transformedEvent.detail.gaEventCategory = 'contrib_thumbnail';
        transformedEvent.detail.gaEventAction = 'thumbnail_contrib_click';
        transformedEvent.detail.gaEventLabel = 'modal_pop';
        break;

      case events.search.filter:
        transformedEvent.detail.gaEventCategory = 'search_filters';
        transformedEvent.detail.gaEventAction = this.transformAnalyticsSearchFilterAction(event.detail.filter);
        transformedEvent.detail.gaEventLabel = event.detail.searchTerm;
        transformedEvent.detail.gaEventValue = event.detail.filterValue;
        break;

      case events.search.hide_show_search_filters:
        transformedEvent.detail.gaEventCategory = 'search_filters';
        transformedEvent.detail.gaEventAction = event.detail.action;
        transformedEvent.detail.gaEventLabel = event.detail.searchTerm;
        break;

      case events.search.search_type:
        transformedEvent.detail.gaEventCategory = 'Search';
        transformedEvent.detail.gaEventAction = this.transformAnalyticsSearchType(event.detail);
        transformedEvent.detail.gaEventLabel = event.detail.searchTerm;
        break;

      case events.search.without_results:
        transformedEvent.detail.gaEventCategory = 'search_without_results';
        transformedEvent.detail.gaEventAction = event.detail.searchTerm;
        break;

      case events.search.trending_search_click:
        transformedEvent.detail.gaEventCategory = 'search_trending_search_click';
        transformedEvent.detail.gaEventAction = event.detail.searchTerm;
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.sessions.new_account_created:
        transformedEvent.detail.gaEventCategory = 'account';
        transformedEvent.detail.gaEventAction = 'new account created';
        transformedEvent.detail.gaEventLabel = this.transformAnalyticsProToPremium(event.detail.accountType);
        break;

      case events.sessions.user_signup_survey:
        transformedEvent.detail.gaEventCategory = 'new_user_flow';
        transformedEvent.detail.gaEventAction = 'use_vecteezy_for_question';
        transformedEvent.detail.gaEventLabel = this.transformAnalyticsPurpose(event.detail.purpose);
        break;

      case events.sessions.social.login_with_google:
        transformedEvent.detail.gaEventCategory = 'login';
        transformedEvent.detail.gaEventAction = 'login_with_google';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.sessions.social.login_with_facebook:
        transformedEvent.detail.gaEventCategory = 'login';
        transformedEvent.detail.gaEventAction = 'login_with_facebook';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.sessions.social.create_account_with_google:
        transformedEvent.detail.gaEventCategory = 'create_account';
        transformedEvent.detail.gaEventAction = 'create_account_with_google';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.sessions.social.create_account_with_facebook:
        transformedEvent.detail.gaEventCategory = 'create_account';
        transformedEvent.detail.gaEventAction = 'create_account_with_facebook';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.attribution_link_copied:
        transformedEvent.detail.gaEventCategory = 'attribution';
        transformedEvent.detail.gaEventAction = 'copy_attribution';
        break;

      case events.show_page.attribution_join_pro_click:
        transformedEvent.detail.gaEventCategory = 'attribution';
        transformedEvent.detail.gaEventAction = 'join pro';
        break;

      case events.show_page.contributor_click:
        transformedEvent.detail.gaEventCategory = 'show_page';
        transformedEvent.detail.gaEventAction = 'contributor_info';
        transformedEvent.detail.gaEventLabel = event.detail.contributor_name;
        break;

      case events.show_page.download_ad_modal_connects:
        transformedEvent.detail.gaEventCategory = 'Show_640x480_DnldPopup';
        transformedEvent.detail.gaEventAction = 'Show_640x480_DnldPopup';
        break;

      case events.show_page.download_limit_modal_connects:
        transformedEvent.detail.gaEventCategory = 'pop_up triggered';
        transformedEvent.detail.gaEventAction = '3 downloads';
        break;

      case events.show_page.favorite:
        Object.assign(transformedEvent.detail, {
          gaEventCategory: this.showPageEventCategory(transformedEvent.detail.resourceContentType),
          gaEventAction: 'favorite',
          gaEventLabel: window.location.pathname,
        });
        break;

      case events.show_page.editor_button_click:
        transformedEvent.detail.gaEventCategory = 'show_page';
        transformedEvent.detail.gaEventAction = 'edit button click';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.file_delivered:
        transformedEvent.detail.gaEventCategory = 'show_page';
        transformedEvent.detail.gaEventAction = 'file_delivered';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.license_selector_free_modal_closes:
        transformedEvent.detail.gaEventCategory = 'post_download_license_popup';
        transformedEvent.detail.gaEventAction = 'user_close';
        break;

      case events.show_page.license_selector_free_modal_connects:
        transformedEvent.detail.gaEventCategory = 'post_download_license_popup';
        transformedEvent.detail.gaEventAction = 'pop_up';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.license_selector_free_modal_free_button:
        transformedEvent.detail.gaEventCategory = 'post_download_license_popup';
        transformedEvent.detail.gaEventAction = this.transformAnalyticsLicenseType(event.detail.licenseType);
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.license_selector_free_modal_learn_more:
        transformedEvent.detail.gaEventCategory = 'post_download_license_popup';
        transformedEvent.detail.gaEventAction = 'premium_license_learn_more';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.license_selector_free_modal_pro_button:
        transformedEvent.detail.gaEventCategory = 'post_download_license_popup';
        transformedEvent.detail.gaEventAction = this.transformAnalyticsLicenseType(event.detail.licenseType);
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.more_info:
        Object.assign(transformedEvent.detail, {
          gaEventCategory: this.showPageEventCategory(transformedEvent.detail.resourceContentType),
          gaEventAction: 'info',
          gaEventLabel: window.location.pathname,
        });
        break;

      case events.show_page.share:
        transformedEvent.detail.gaEventCategory = this.showPageEventCategory(event.detail.resourceContentType);
        transformedEvent.detail.gaEventAction = 'share';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.share_facebook:
        transformedEvent.detail.gaEventCategory = this.showPageEventCategory(event.detail.resourceContentType);
        transformedEvent.detail.gaEventAction = 'share_facebook';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.share_html:
        transformedEvent.detail.gaEventCategory = this.showPageEventCategory(event.detail.resourceContentType);
        transformedEvent.detail.gaEventAction = 'share_copy_html';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.show_page.share_pinterest:
        Object.assign(transformedEvent.detail, {
          gaEventCategory: this.showPageEventCategory(transformedEvent.detail.resourceContentType),
          gaEventAction: 'share_pinterest',
          gaEventLabel: window.location.pathname,
        });
        break;

      case events.show_page.share_twitter:
        Object.assign(transformedEvent.detail, {
          gaEventCategory: this.showPageEventCategory(transformedEvent.detail.resourceContentType),
          gaEventAction: 'share_twitter',
          gaEventLabel: window.location.pathname,
        });
        break;

      case events.show_page.zoom_in:
        Object.assign(transformedEvent.detail, {
          gaEventCategory: this.showPageEventCategory(transformedEvent.detail.resourceContentType),
          gaEventAction: 'zoom_in',
          gaEventLabel: window.location.pathname,
        });
        break;

      case events.site_header.explore_nav:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'explore_click';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.site_header.explore_newest:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'explore_click';
        transformedEvent.detail.gaEventLabel = 'browse_newest';
        break;

      case events.site_header.explore_cats:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'explore_click';
        transformedEvent.detail.gaEventLabel = 'browse_by_category';
        break;

      case events.site_header.explore_brusheezy:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'explore_click';
        transformedEvent.detail.gaEventLabel = 'brusheezy';
        break;

      case events.site_header.explore_videezy:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'explore_click';
        transformedEvent.detail.gaEventLabel = 'videezy';
        break;

      case events.site_header.header_join_pro:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'go_premium';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.site_header.header_login:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'login';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.site_header.header_signup:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'join_eezy';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.site_header.header_credits_buy_more:
        transformedEvent.detail.gaEventCategory = 'header';
        transformedEvent.detail.gaEventAction = 'credits_buy_more';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.site_header.related_tag_click:
        transformedEvent.detail.gaEventCategory = 'tag_click';
        transformedEvent.detail.gaEventAction = event.detail.tagClicked;
        transformedEvent.detail.gaEventLabel = event.detail.tagSearched;
        break;

      case events.show_page.related_resource_click:
        transformedEvent.detail.gaEventCategory = 'show_page';
        transformedEvent.detail.gaEventAction = 'related_resource'; // check this again
        transformedEvent.detail.gaEventLabel = 'click_'.concat(event.detail.resourceId);
        break;

      case events.social.pinterest_share:
        transformedEvent.detail.gaEventCategory = 'pinterest';
        transformedEvent.detail.gaEventAction = this.transformPinType(event.detail.btnLocation);
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.sponsored_search.without_results:
        transformedEvent.detail.gaEventCategory = 'sponsored_search_without_results';
        transformedEvent.detail.gaEventAction = event.detail.searchTerm;
        break;

      case events.partner.coupon_click:
        transformedEvent.detail.gaEventCategory = 'out_shutterstock';
        transformedEvent.detail.gaEventAction = this.transformGreyBoxPageContext(event.detail.pageType, 'grey_box_coupon');
        transformedEvent.detail.gaEventLabel = event.detail.pageTypeLabel;
        break;

      case events.partner.pagination_popup:
        transformedEvent.detail.gaEventCategory = 'sstk_popup';
        transformedEvent.detail.gaEventAction = 'pagination';
        break;

      case events.partner.results_click:
        transformedEvent.detail.gaEventCategory = 'out_shutterstock';
        transformedEvent.detail.gaEventAction = this.transformGreyBoxPageContext(event.detail.pageType, 'grey_box');
        transformedEvent.detail.gaEventLabel = event.detail.pageTypeLabel;
        break;

      case events.partner.infinite_scroll_click:
        transformedEvent.detail.gaEventCategory = 'out_shutterstock';
        transformedEvent.detail.gaEventAction = this.transformGreyBoxPageContext(
          event.detail.pageType, 'infinite_scroll',
        );
        transformedEvent.detail.gaEventLabel = event.detail.pageTypeLabel;
        break;

      case events.partner.search_popup:
        transformedEvent.detail.gaEventCategory = 'sstk_popup';
        transformedEvent.detail.gaEventAction = event.detail.subId1;
        transformedEvent.detail.gaEventLabel = event.detail.subId2;
        break;

      case events.checkout.error:
        transformedEvent.detail.gaEventCategory = 'ecommerce';
        transformedEvent.detail.gaEventAction = 'error';
        transformedEvent.detail.gaEventValue = event.detail.message;
        break;

      case events.checkout.purchase:
        transformedEvent.detail.gaEventCategory = 'pro_purchase';
        transformedEvent.detail.gaEventAction = event.detail.plan;
        transformedEvent.detail.gaEventLabel = event.detail.currency;
        transformedEvent.detail.gaEventValue = event.detail.transactionAmount;
        break;

      case events.checkout.inputs.focus:
        transformedEvent.detail.gaEventCategory = 'checkout_input_event:focus';
        transformedEvent.detail.gaEventAction = event.detail.elementFor;
        break;

      case events.checkout.inputs.nonHostedFocus:
        transformedEvent.detail.gaEventCategory = 'checkout_input_event:non_hosted_focus';
        transformedEvent.detail.gaEventAction = event.detail.elementFor;
        break;

      case events.checkout.inputs.clickSubmitDisabled:
        transformedEvent.detail.gaEventCategory = 'checkout_input_event:click_submit_disabled';
        transformedEvent.detail.gaEventAction = event.detail.elementFor;
        break;

      case events.checkout.inputs.clickSubmitEnabled:
        transformedEvent.detail.gaEventCategory = 'checkout_input_event:click_submit_enabled';
        transformedEvent.detail.gaEventAction = event.detail.elementFor;
        break;

      case events.checkout.inputs.error:
        transformedEvent.detail.gaEventCategory = 'checkout_input_event:error';
        transformedEvent.detail.gaEventAction = event.detail.elementFor;
        transformedEvent.detail.gaEventLabel = event.detail.message;
        break;

      case events.search.header_search_filter:
        transformedEvent.detail.gaEventCategory = 'header_search_filter';
        transformedEvent.detail.gaEventAction = event.detail.filterValue;
        transformedEvent.detail.gaEventLabel = event.detail.searchTerm;
        break;

      case events.collections.resource_added:
        transformedEvent.detail.gaEventCategory = 'collections';
        transformedEvent.detail.gaEventAction = 'added_to_collection';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.collections.resource_removed:
        transformedEvent.detail.gaEventCategory = 'collections';
        transformedEvent.detail.gaEventAction = 'removed_from_collection';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.collections.add_clicked:
        transformedEvent.detail.gaEventCategory = 'collections';
        transformedEvent.detail.gaEventAction = 'save_to_collection';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.collections.created:
        transformedEvent.detail.gaEventCategory = 'collections';
        transformedEvent.detail.gaEventAction = 'create_new_collection';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.collections.login_popup:
        transformedEvent.detail.gaEventCategory = 'login_pop';
        transformedEvent.detail.gaEventAction = 'pop';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.collections.account_create:
        transformedEvent.detail.gaEventCategory = 'login_pop';
        transformedEvent.detail.gaEventAction = 'create_an_acount_click';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.ten_download_limit.join_pro:
        transformedEvent.detail.gaEventCategory = '10_download_lock';
        transformedEvent.detail.gaEventAction = 'join_pro';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.ten_download_limit.open:
        transformedEvent.detail.gaEventCategory = '10_download_lock';
        transformedEvent.detail.gaEventAction = 'pop';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.three_download_limit.control_facebook_join:
        transformedEvent.detail.gaEventCategory = 'want_faster_downloads_3';
        transformedEvent.detail.gaEventAction = 'sign_up_with_facebook';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.three_download_limit.control_google_join:
        transformedEvent.detail.gaEventCategory = 'want_faster_downloads_3';
        transformedEvent.detail.gaEventAction = 'sign_up_with_google';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.three_download_limit.control_pop:
        transformedEvent.detail.gaEventCategory = 'want_faster_downloads_3';
        transformedEvent.detail.gaEventAction = 'pop';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.three_download_limit.control_step_one_click:
        transformedEvent.detail.gaEventCategory = 'want_faster_downloads_3';
        transformedEvent.detail.gaEventAction = 'create_free_account_step1';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.three_download_limit.control_step_two_click:
        transformedEvent.detail.gaEventCategory = 'want_faster_downloads_3';
        transformedEvent.detail.gaEventAction = 'create_free_account_step2';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.three_download_limit.facebook_join:
        transformedEvent.detail.gaEventCategory = '3_download_lock';
        transformedEvent.detail.gaEventAction = 'sign_up_with_facebook';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.three_download_limit.google_join:
        transformedEvent.detail.gaEventCategory = '3_download_lock';
        transformedEvent.detail.gaEventAction = 'sign_up_with_google';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.three_download_limit.login_click:
        transformedEvent.detail.gaEventCategory = '3_download_lock';
        transformedEvent.detail.gaEventAction = 'switch_to_login';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.three_download_limit.open:
        transformedEvent.detail.gaEventCategory = '3_download_lock';
        transformedEvent.detail.gaEventAction = 'pop';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.three_download_limit.step_one_click:
        transformedEvent.detail.gaEventCategory = '3_download_lock';
        transformedEvent.detail.gaEventAction = 'sign_up_continue';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.three_download_limit.step_two_click:
        transformedEvent.detail.gaEventCategory = '3_download_lock';
        transformedEvent.detail.gaEventAction = 'create_free_account_step2';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.three_download_limit.unconfirmed_open:
        transformedEvent.detail.gaEventCategory = '3_download_lock';
        transformedEvent.detail.gaEventAction = 'confirm_email_pop';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.three_download_limit.unconfirmed_send:
        transformedEvent.detail.gaEventCategory = '3_download_lock';
        transformedEvent.detail.gaEventAction = 'resend_confirm_email';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.vertical_grid.show_more_click:
        transformedEvent.detail.gaEventCategory = 'vertical_grid';
        transformedEvent.detail.gaEventAction = this.transformVerticalGridAction('show_more_click', event.detail.pageType);
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.vertical_grid.end_of_results:
        transformedEvent.detail.gaEventCategory = 'vertical_grid';
        transformedEvent.detail.gaEventAction = this.transformVerticalGridAction('end_of_results', event.detail.pageType);
        transformedEvent.detail.gaEventLabel = event.detail.url;
        break;

      // feature photos_banner
      case events.photos_banner.photos_banner_dismissed:
        transformedEvent.detail.gaEventCategory = 'photos_launch_bar_announcement';
        transformedEvent.detail.gaEventAction = 'learn_more';
        transformedEvent.detail.gaEventLabel = 'dismiss';
        break;

      // feature photos_banner
      case events.photos_banner.photos_banner_learn_more:
        transformedEvent.detail.gaEventCategory = 'photos_launch_bar_announcement';
        transformedEvent.detail.gaEventAction = 'learn_more';
        transformedEvent.detail.gaEventLabel = window.location.pathname;
        break;

      case events.contributor.following:
        transformedEvent.detail.gaEventCategory = event.detail.category;
        transformedEvent.detail.gaEventAction = event.detail.username;
        transformedEvent.detail.gaEventLabel = window.location.href;
        break;

      default:
        /* Do nothing */
    }

    /* Split test analytics */
    switch (event.type) {
      case events.generic.ez_toggle_change:
        if (event.detail.changedTo === 'monthly') transformedEvent.detail.gaEventLabel = 'monthly';
        else if (event.detail.changedTo === 'annual') transformedEvent.detail.gaEventLabel = 'yearly';
        else return false;

        transformedEvent.detail.gaEventCategory = 'pricing_page';
        transformedEvent.detail.gaEventAction = 'monthly_yearly_slider';
        break;

      default:
        break;
    }

    if (transformedEvent.detail) {
      this.trackEvent(transformedEvent);
    }

    return true;
  }

  trackPageView = (url) => {
    const pageViewUrl = this.resourceType === 'pro' ? `${url}/premium` : url;
    this.ga('set', 'location', url);
    this.ga('send', 'pageview', this.pathFromUrl(pageViewUrl));
  }

  trackEvent = (event) => {
    const fieldsObject = {
      hitType: 'event',
      nonInteraction: true,
    };

    /* refactor with Object.assign */
    if (Object.prototype.hasOwnProperty.call(event.detail, 'gaEventCategory')) {
      fieldsObject.eventCategory = event.detail.gaEventCategory;
    }

    if (Object.prototype.hasOwnProperty.call(event.detail, 'gaEventAction')) {
      fieldsObject.eventAction = event.detail.gaEventAction;
    }

    if (Object.prototype.hasOwnProperty.call(event.detail, 'gaEventLabel')) {
      fieldsObject.eventLabel = event.detail.gaEventLabel;
    }

    if (Object.prototype.hasOwnProperty.call(event.detail, 'gaEventValue')) {
      fieldsObject.eventValue = event.detail.gaEventValue;
    }

    if (Object.prototype.hasOwnProperty.call(event.detail, 'gaNonInteraction')) {
      fieldsObject.nonInteraction = event.detail.gaNonInteraction;
    }

    window.snowplow('trackStructEvent',
      fieldsObject.eventCategory,
      fieldsObject.eventAction,
      fieldsObject.eventLabel,
      null, // property (string)
      null, // value (float)
      this.snowplow.getCustomContexts());

    if (document.body.getAttribute('data-env') === 'test') {
      const googleEventsSent = Cookies.getJSON('googleEventsSent') || [];
      googleEventsSent.push(fieldsObject);
      Cookies.set('googleEventsSent', googleEventsSent, { domain: '.example.com' });
    }

    // Push GA events to dataLayer for use by GTM
    window.dataLayer.push({ event: event.type, ...fieldsObject });
    this.ga('send', fieldsObject);
  }

  /* eslint-disable */
  // See https://developers.google.com/analytics/devguides/collection/analyticsjs/ for "inspiration"
  loadGA = function (i, s, o, g, r, a, m) {
    i['GoogleAnalyticsObject'] = r;
    i[r] = i[r] || function () {
      (i[r].q = i[r].q || []).push(arguments);
    }, i[r].l = 1 * new Date();
    a = s.createElement(o),
    m = s.getElementsByTagName(o)[0];
    a.async = 1;
    a.src = g;
    m.parentNode.insertBefore(a, m);
  };
  /* eslint-enable */

  ga(...args) {
    if (args[0] === 'send' && args[1] === 'event') {
      args.push({ nonInteraction: true });
    }

    window.ga(...args);
  }

  get scriptUrl() {
    return 'https://www.google-analytics.com/analytics.js';
  }

  get site() {
    return this.element.getAttribute('data-site');
  }

  get trackingId() {
    return this.element.getAttribute('data-tracking-id');
  }

  get userDimension() {
    return this.element.getAttribute('data-user-dimension-4');
  }

  get userDimension5() {
    return this.element.getAttribute('data-user-dimension-5');
  }

  get userDimension7() {
    return this.element.getAttribute('data-user-dimension-7');
  }

  get userDimension8() {
    return this.element.getAttribute('data-user-dimension-8');
  }

  get userDimension9() {
    return this.element.getAttribute('data-user-dimension-9');
  }

  get optimizeContainerID() {
    return this.element.getAttribute('data-optimize-container');
  }

  get optimizeExperimentID() {
    return this.element.getAttribute('data-optimize-experiment');
  }

  get optimizeVariantID() {
    return this.element.getAttribute('data-optimize-variant');
  }

  get snowplowCollector() {
    return this.element.getAttribute('data-snowplow-collector');
  }

  get trackResourceImpressions() {
    return this.element.getAttribute('data-track-resource-impressions') === 'true';
  }

  get snowplowAppId() {
    return this.element.getAttribute('data-snowplow-app-id');
  }

  showPageEventCategory(contentType) {
    if (contentType === 'photo') {
      return 'photos_show_page';
    }

    return 'show_page';
  }

  transformAnalyticsProToPremium(eventProp) {
    if (eventProp === 'pro') {
      return 'premium';
    }

    return eventProp;
  }

  transformAnalyticsPurpose(eventPurpose) {
    const purpose = eventPurpose.replace(/[()]+/g, '').split(' ').join('_');
    return purpose;
  }

  transformAnalyticsLicenseType(eventProp) {
    if (eventProp === 'pro_license') {
      return 'premium_license';
    }

    return eventProp;
  }

  transformAnalyticsSearchType(detail) {
    const types = {
      internal: 'Internal',
      external: 'External',
    };

    const base = types[detail.searchType];
    if (!base) {
      console.error('unknown searchType: %s', detail.searchType); /* eslint-disable-line  no-console */
      return null;
    }

    if (detail.searchContentType && detail.searchContentType !== 'vector') {
      return `${base}-${detail.searchContentType}`;
    }
    return base;
  }

  transformAnalyticsSearchFilterAction(filter) {
    const actions = {
      editor: 'filter_by_editor_compatible',
      'license-editorial': 'filter_by_license_type_editorial_use_only',
      'license-free': 'filter_by_license_type_standard',
      'license-pro': 'filter_by_license_type_premium',
      'orientation-horizontal': 'filter_by_orientation_horizontal',
      'orientation-square': 'filter_by_orientation_square',
      'orientation-vertical': 'filter_by_orientation_vertical',
      'sort-best': 'sort_by_best_match',
      'sort-newest': 'sort_by_newest',
    };

    return actions[filter];
  }

  transformVerticalGridAction(action, pageType) {
    return `${action}--${pageType}`;
  }

  trimPathFromURL(url) {
    const { protocol, host } = window.location;
    const toTrim = `${protocol}//${host}`;
    const newUrl = url.substring(toTrim.length);
    return newUrl;
  }

  transformGreyBoxPageContext(pageContext, action) {
    const transformedPageContext = pageContext === 'show' ? 'show_page' : pageContext;
    return `${transformedPageContext}_${action}`;
  }

  transformPinType(pinType) {
    let value;

    switch (pinType) {
      case 'preview':
        value = 'pin_show_preview_large';
        break;

      default:
        value = 'pin_type_not_found';
        break;
    }
    return value;
  }

  pathFromUrl(pageUrl) {
    const url = new URL(pageUrl);

    if (this.searchForm) {
      url.searchParams.set('site-search', this.searchTerm);
      url.searchParams.set('search-scope', this.transformAnalyticsSearchType({ searchType: this.searchType }));
    }

    return `${url.pathname}${url.search}`;
  }

  get searchForm() {
    const form = document.querySelector('[data-controller~="search-form"]');
    if (form && form.getAttribute('data-search-term')) {
      return form;
    }

    return null;
  }

  get searchPage() {
    const currentPageElement = document.querySelector('[data-target="ez-num-pager.currentPage"]');
    let currentPage;
    if (currentPageElement) {
      currentPage = parseInt(currentPageElement.innerHTML, 10);
    }

    if (Number.isNaN(currentPage)) {
      return 1;
    }

    return currentPage;
  }

  get searchTerm() {
    if (this.searchForm) {
      return this.searchForm.getAttribute('data-search-term');
    }
    return null;
  }

  get searchType() {
    if (this.searchForm) {
      return this.searchForm.getAttribute('data-search-type');
    }
    return null;
  }

  get isTurbolinksPreview() {
    return document.documentElement.hasAttribute('data-turbolinks-preview');
  }

  get resourceType() {
    return this.element.getAttribute('data-resource-type');
  }
}
